/**
 * @fileoverview Shopify Cart API utilities
 * @module utils/cart
 *
 * Provides a clean interface for interacting with Shopify's Cart API.
 * All functions return promises and handle errors gracefully.
 */

const CART_ROUTES = {
  get: '/cart.js',
  add: '/cart/add.js',
  update: '/cart/update.js',
  change: '/cart/change.js',
  clear: '/cart/clear.js',
};

/**
 * Base fetch configuration for Shopify cart API
 * @returns {RequestInit} Fetch configuration object
 */
function getBaseConfig() {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };
}

/**
 * Makes a request to Shopify cart API
 * @param {string} url - API endpoint
 * @param {Object} [data] - Request body
 * @returns {Promise<{items?: Array<any>, [key: string]: any}>} Response data
 */
async function cartRequest(url, data = undefined) {
  const config = {
    ...getBaseConfig(),
    method: data ? 'POST' : 'GET',
  };

  if (data) {
    config.body = JSON.stringify(data);
  }

  const response = await fetch(url, config);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.description || 'Cart request failed');
  }

  return response.json();
}

/**
 * Dispatches a custom cart event
 * @param {string} eventName - Name of the event
 * @param {Object} detail - Event detail data
 */
function dispatchCartEvent(eventName, detail) {
  document.dispatchEvent(
    new CustomEvent(`rt-cart:${eventName}`, {
      bubbles: true,
      detail,
    })
  );
}

/**
 * Fetches the current cart state
 * @returns {Promise<Object>} Cart object
 */
export async function getCart() {
  const cart = await cartRequest(CART_ROUTES.get);
  dispatchCartEvent('fetched', { cart });
  return cart;
}

/**
 * Adds items to the cart
 * @param {Object} item - Item to add
 * @param {number} item.id - Variant ID
 * @param {number} item.quantity - Quantity to add
 * @param {Object} [item.properties] - Line item properties
 * @returns {Promise<{items: Array<any>, [key: string]: any}>} Updated cart items
 */
export async function addToCart(item) {
  const items = Array.isArray(item) ? item : [item];
  const result = await cartRequest(CART_ROUTES.add, { items });
  const cartResult = { ...result, items: result?.items || [] };
  dispatchCartEvent('added', { items: cartResult.items });
  return cartResult;
}

/**
 * Updates cart item quantities
 * @param {Object} updates - Object with line item keys and quantities
 * @returns {Promise<Object>} Updated cart
 */
export async function updateCart(updates) {
  const cart = await cartRequest(CART_ROUTES.update, { updates });
  dispatchCartEvent('updated', { cart });
  return cart;
}

/**
 * Changes a specific line item
 * @param {Object} change - Change object
 * @param {string} change.id - Line item key
 * @param {number} change.quantity - New quantity
 * @returns {Promise<Object>} Updated cart
 */
export async function changeCartItem(change) {
  const cart = await cartRequest(CART_ROUTES.change, change);
  dispatchCartEvent('changed', { cart, change });
  return cart;
}

/**
 * Removes an item from the cart
 * @param {string} lineItemKey - Line item key to remove
 * @returns {Promise<Object>} Updated cart
 */
export async function removeFromCart(lineItemKey) {
  return changeCartItem({ id: lineItemKey, quantity: 0 });
}

/**
 * Clears all items from the cart
 * @returns {Promise<Object>} Empty cart
 */
export async function clearCart() {
  const cart = await cartRequest(CART_ROUTES.clear);
  dispatchCartEvent('cleared', { cart });
  return cart;
}

/**
 * Updates cart attributes
 * @param {Object} attributes - Attributes to set
 * @returns {Promise<Object>} Updated cart
 */
export async function updateCartAttributes(attributes) {
  return cartRequest(CART_ROUTES.update, { attributes });
}

/**
 * Updates cart note
 * @param {string} note - Note to set
 * @returns {Promise<Object>} Updated cart
 */
export async function updateCartNote(note) {
  return cartRequest(CART_ROUTES.update, { note });
}
